import { basename, dirname } from 'path';
import { generateArrayFromPath } from './generateArrayFromPath';
import { isFileHidden } from './isFileHidden';
import type { UploadFileItem, UploadFolderItem } from './uploadItem';
import { createFileItem, createFolderItem } from './uploadItem';

export function getUploadItemsFromFiles(files: File[], endIndex = 0) {
  // Folder path -> id cache
  const folderPathToId: Record<string, string> = {};

  // The current sort index used in that folder
  const parentFolderPathToSortIndex: Record<string, number> = {
    '.': endIndex,
  };

  const visibleFiles = Array.from(files).filter(
    (file) => !isFileHidden(file.name)
  );
  const filesStartIndex = endIndex - visibleFiles.length + 1;

  /**
   * Given a FS path, create all folders in the hierarcy and sync the sortIndex
   * tracker.
   *
   * @param {string} path - File system path.
   * @returns {Items[]} - An array of folder items.
   */
  function createAllFoldersForPath(path: string) {
    const folderItems: Array<UploadFolderItem> = [];
    const folderNames = path.split('/');
    folderNames.forEach((folderName, dirLevel) => {
      const folderPath = folderNames.slice(0, dirLevel + 1).join('/');
      if (folderPathToId[folderPath]) return;

      const parentFolderPath = dirname(folderPath);
      const isRoot = parentFolderPath === '.';
      const parentFolderId = isRoot ? null : folderPathToId[parentFolderPath];

      const folderItem = createFolderItem(
        parentFolderId,
        folderName,
        parentFolderPathToSortIndex[parentFolderPath],
        generateArrayFromPath(folderPath)
      );
      folderItems.push(folderItem);

      // Save reference to the folder
      folderPathToId[folderPath] = folderItem.tempId;

      // Initialize sortIndex for the newly-created folder
      parentFolderPathToSortIndex[folderPath] = 0;

      // Increment index for the next folder/file
      parentFolderPathToSortIndex[parentFolderPath] += 1;
    });

    return folderItems;
  }

  const items: Array<UploadFileItem | UploadFolderItem> = [];
  visibleFiles.forEach((file, index) => {
    let fileItem;
    // @ts-ignore
    if (file.webkitRelativePath) {
      // @ts-ignore
      const parentFolderPath = dirname(file.webkitRelativePath);
      let parentFolderId = folderPathToId[parentFolderPath];

      // Filter out hidden folders
      const name = basename(parentFolderPath);
      if (isFileHidden(name)) return;

      // Create the folder that this file will be put in, and any folders
      // between the file and the root folder.
      if (!parentFolderId) {
        const folderItems = createAllFoldersForPath(parentFolderPath);
        items.push(...folderItems);

        parentFolderId = folderPathToId[parentFolderPath];
      }

      // Add the file to the folder
      fileItem = createFileItem(
        parentFolderId,
        file,
        parentFolderPathToSortIndex[parentFolderPath]
      );

      // Increment index for the next folder/file
      parentFolderPathToSortIndex[parentFolderPath] += 1;
    } else {
      fileItem = createFileItem(null, file, filesStartIndex + index);
    }

    items.push(fileItem);
  });

  return items;
}
